import { Injectable } from '@angular/core';
import Intercom from '@intercom/messenger-js-sdk';
import { GoogleTagManagerService } from 'angular-google-tag-manager';

import { AuthService } from '../../lib/lib-ngx/web-services/auth.service';
import { environment } from '../../environments/environment';
import { EnrichedAddress } from './address.service';

export type OnboardingStep = 'address' | 'pickup' | 'form1' | 'form2';

export type OrderTrackingType = 'kit' | 'pre_order' | 'book';

// Tracked kit names by kit id, the names coming from the API may change
const KIT_TRACKING_NAMES: Record<number, string> = {
  1: 'drap',
  2: 'couette',
};

// ISO 3166-1 alpha-2 codes of the countries stored as Google Maps long names
const COUNTRY_CODES: Record<string, string> = {
  france: 'FR',
  belgique: 'BE',
  belgium: 'BE',
  suisse: 'CH',
  switzerland: 'CH',
  luxembourg: 'LU',
  monaco: 'MC',
};

@Injectable({
  providedIn: 'root',
})
export class UserTrackingService {
  private gtmLoaded?: Promise<boolean>;

  constructor(
    private authService: AuthService,
    private gtmService: GoogleTagManagerService
  ) {
    this.authService.logout$.subscribe(() => {
      // Reset the user data kept by the GTM data model since the previous pushes
      this.pushTag({
        event: 'logout',
        user_id: undefined,
        email: undefined,
        user_data: undefined,
      });
    });
  }

  /**
   * Load GTM only once: the library adds GTM again (script and gtm.js event)
   * when a tag is pushed before the end of the previous loading
   */
  loadGtm(): Promise<boolean> {
    this.gtmLoaded ??= this.gtmService.addGtmToDom();
    return this.gtmLoaded;
  }

  /**
   * Also pushed for an anonymous visitor, so that every page load has a first app event
   */
  trackIdentify(): void {
    if (!this.authService.isLoggedIn()) {
      this.pushTag({ event: 'identify' });
      return;
    }
    const token = this.authService.getDecodeToken();
    if (environment.production) {
      Intercom({
        app_id: 'uyrkx3p4',
        user_id: token.id.toString(),
        email: token.email,
      });
    }
    this.pushTag({
      event: 'identify',
      user_id: token.id.toString(),
      email: token.email,
    });
  }

  trackLogin(): void {
    this.trackIdentify();
    this.pushTag({ event: 'login' });
  }

  trackSignUp(): void {
    this.trackIdentify();
    this.pushTag({
      event: 'sign_up',
      registration_method: 'email',
    });
  }

  trackOnboardingStep(step: OnboardingStep): void {
    this.pushTag({ event: `onboarding_step_${step}` });
  }

  /**
   * Track a confirmed order, the delivery address is sent only when kits are delivered
   */
  trackOrder(params: {
    type: OrderTrackingType;
    kitsQuantity?: Record<number, number>;
    value?: number;
    deliveryAddress?: EnrichedAddress;
  }): void {
    const kits = Object.entries(params.kitsQuantity ?? {})
      .filter(([, quantity]) => quantity > 0)
      .map(([id, quantity]) => ({
        name: KIT_TRACKING_NAMES[parseInt(id)] ?? `kit_${id}`,
        quantity,
      }));

    this.pushTag({
      event: 'order',
      type: params.type,
      kits,
      ...(params.value !== undefined && {
        value: Math.round(params.value * 100) / 100,
        currency: 'EUR',
      }),
      ...(kits.length > 0 &&
        params.deliveryAddress && {
          user_data: { address: this.getTrackingAddress(params.deliveryAddress) },
        }),
    });
  }

  /**
   * Wait for GTM to be loaded so that the tags are pushed after gtm.js and in their call order
   */
  private pushTag(item: object): void {
    this.loadGtm()
      .then(() => this.gtmService.pushTag(item))
      .catch(() => {
        // GTM could not be loaded (blocked by an ad blocker...)
      });
  }

  private getTrackingAddress(address: EnrichedAddress): Record<string, string> {
    const token = this.authService.getDecodeToken();
    const trackingAddress: Record<string, string | undefined> = {
      first_name: token.firstName,
      last_name: token.lastName,
      street: `${address.streetNumber ?? ''} ${address.route ?? ''}`.trim(),
      city: address.locality,
      postal_code: address.postalCode,
      country: this.getCountryCode(address.country),
    };
    // Unknown fields must not be sent
    return Object.fromEntries(
      Object.entries(trackingAddress).filter(
        (entry): entry is [string, string] => !!entry[1]
      )
    );
  }

  private getCountryCode(country?: string): string | undefined {
    if (!country) {
      return undefined;
    }
    if (/^[A-Z]{2}$/.test(country)) {
      return country;
    }
    return COUNTRY_CODES[country.toLowerCase()];
  }
}
